// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/AgentSteps.test.tsx
import { createContext, useContext, type ReactNode } from "react";

import {
  type ChatArtifact,
  type ChatArtifactOpenPayload,
  type ChatArtifactSelection,
} from "../../routes/ask-puntt/artifactTypes";

/**
 * Cross-message artifact navigation state for the AskPuntt chat surface.
 */
export interface AskPunttArtifactContextValue {
  activeTicketId: string | null;
  activeReviewId?: string | null;
  activeCanvasMessageId: string | null;
  onActivateTicket: (ticketId: string) => void;
  onActivateReview?: (reviewId: string) => void;
  /** Opens a review straight into its version comparison (Ask v2 only). */
  onCompareReview?: (reviewId: string) => void;
  onActivateCanvas: (messageId: string) => void;
  isAnotherArtifactStreaming: boolean;
  allReviewTicketIds: readonly string[];
  allReviewIds?: readonly string[];
  canvasMessageIds: readonly string[];
  artifacts?: readonly ChatArtifact[];
  selection?: ChatArtifactSelection | null;
  openArtifact?: (
    payload: ChatArtifactOpenPayload,
    source?: "sidebar" | "message_chip",
  ) => void;
  closeArtifact?: () => void;
  /** Unseen `review:<id>` / `canvas:<id>` keys from the chat history snapshot. */
  unseenArtifactKeys?: ReadonlySet<string>;
  /** Clears the inner-chat review dot. v2 passes reviewId, not ticketId. */
  markReviewSeen?: (reviewId: string) => void;
}

const AskPunttArtifactContext =
  createContext<AskPunttArtifactContextValue | null>(null);

export function AskPunttArtifactProvider({
  value,
  children,
}: {
  value: AskPunttArtifactContextValue;
  children: ReactNode;
}) {
  return (
    <AskPunttArtifactContext.Provider value={value}>
      {children}
    </AskPunttArtifactContext.Provider>
  );
}

export function useAskPunttArtifactContext(): AskPunttArtifactContextValue | null {
  return useContext(AskPunttArtifactContext);
}
